import React from "react";
import { useIntl, defineMessages } from "react-intl";
import "../Styles.css";
import * as microsoftTeams from "@microsoft/teams-js";
import DomainPane from "./DomainPane";
import HelpPane from "./HelpPane";
import { debugLog, errorLog } from "../../util/debug";
import { UseGlobalState } from "../../state/gsh/gsh";
import {
  CNXT_ACCESS_TOKEN,
  CNXT_DOMAIN,
  CNXT_JWT,
  CNXT_REFRESH_TOKEN,
  CNXT_TOKEN_EXPIRATION,
  OAUTH_URL,
  PROJECT_PROFILE,
  PREFERRED_LOCALE,
} from "../../constants/storageKeys";
import { useHistory } from "react-router-dom";
import maritzAPIClient from "../../api-clients/maritz";
import axios from "axios";
import { DOMAIN_PANE, NONE, USERPW_PANE } from "./constants";
import UserPWPane from "./UserPWPane";
import { SetBranding } from "../../util/branding";
import { AuthInitialize } from "./api";

export default function Auth(props) {
  const { destination, tabView } = props;
  const [jwt, setJwt] = React.useState("");
  const [invertSSO, setInvertSSO] = React.useState(false);
  const [state, setState] = UseGlobalState();
  const [loading, setLoading] = React.useState(false);
  const history = useHistory();

  const intl = useIntl();
  const messages = defineMessages({
    alertInvalidUrl: {
      id: "app.auth.invalidUrl",
      defaultMessage: "Recognition program URL {value} is not valid",
    },
    alertInvalidUsername: {
      id: "app.auth.invalidUsername",
      defaultMessage: "Recognition username {userName} is not valid",
    },
  });

  const jwtOnChange = (event) => {
    debugLog(event);
    setJwt(event.target.value);
  };

  const jwtKeyPress = (event) => {
    if (event.key === "Enter") {
      console.log("forcing JWT token ", jwt);
      state.jwt = jwt;
      setState(state);
    }
  };

  const handleInvertSSO = (event) => {
    setInvertSSO(event.target.checked);
  };

  // Hook once per component load; if not initialize, handle MS initialization
  React.useEffect(
    () => {
      debugLog("Auth useEffect (empty)");

      if (!state.initialized) {
        AuthInitialize((context) => {
          state.context = context;
          state.tenantId = context.user.tenant.id;
          state.initialized = true;
          setState(state);
        });
      }
    },
    // eslint-disable-next-line
    []
  );

  // Hook when initialized flips true; determine which panel should be displayed
  React.useEffect(
    () => {
      debugLog("Auth useEffect (state.initialized) : ", state.initialized);
      if (state.initialized) {
        debugLog("Choosing panel...");
        const domain = window.localStorage.getItem(CNXT_DOMAIN);
        const profileStr = window.localStorage.getItem(PROJECT_PROFILE);
        if (!!!domain || !!!profileStr) {
          state.loginPane = DOMAIN_PANE;
          debugLog("Selected Domain Panel...");
          setState(state);
        } else {
          const token = window.localStorage.getItem(CNXT_ACCESS_TOKEN);
          const exp = window.localStorage.getItem(CNXT_TOKEN_EXPIRATION);
          if (!!token && !!exp && exp > new Date().getTime() && !!!tabView) {
            setTimeout(() => {
              toRecognitionRoute();
              debugLog("Selected Recognition Panel...");
            }, 500);
          } else {
            const profile = JSON.parse(profileStr);
            if (profile.teams.sso) {
              attemptTeamsAuth(profile.teams.sso);
            } else {
              debugLog("Selected Login Panel...");
              state.loginPane = USERPW_PANE;
              state.selectedDomain = domain;
              setState(state);
            }
          }
        }
      }
    },
    // eslint-disable-next-line
    [state.initialized]
  );

  const attemptWebAuth = async (url) => {
    debugLog("attempting web direct auth:", url);
    window.location.replace(url);
  };

  const attemptTeamsAuth = async (url) => {
    debugLog("auth url: ", url);
    window.localStorage.setItem(OAUTH_URL, url);

    setLoading(true);

    const authUrl = `${window.location.origin}/#/auth-start/${encodeURIComponent(url)}`;
    debugLog("sending to auth url: " + authUrl);
    microsoftTeams.authentication.authenticate({
      url: authUrl,
      isExternal: false,
      width: 600,
      height: 535
    }).then((result) => {
      handleAuthSuccess(result.jwt);
    }).catch((reason) => {
      if (state.jwt.length > 0) {
        errorLog("over riding fail fuction");
        handleAuthSuccess(state.jwt);
        return;
      }
      setLoading(false);
      errorLog("teams fail callback: ", reason);
      alert("Authentication FAILED: " + JSON.stringify(reason));
    });
  };

  const handleAuthSuccess = async (jwt) => {
    debugLog("teams success callback");
    window.localStorage.setItem(CNXT_JWT, jwt);

    setLoading(true);

    // If in tab view; don't do anything with our new JWT
    if (!!tabView) {
      toRecognitionRoute();
      return;
    }

    const maritz = new maritzAPIClient();
    const tokenResult = await maritz.redeemJWTForApigeeToken(jwt);
    debugLog("apigee token request: ", tokenResult);

    if (!!tokenResult?.access_token) {
      window.localStorage.setItem(
        PREFERRED_LOCALE,
        tokenResult.preferredLocale
      );
      window.localStorage.setItem(CNXT_ACCESS_TOKEN, tokenResult.access_token);
      if (!!tokenResult?.refresh_token) {
        window.localStorage.setItem(
          CNXT_REFRESH_TOKEN,
          tokenResult.refresh_token
        );
      } else {
        window.localStorage.removeItem(CNXT_REFRESH_TOKEN);
      }
      window.localStorage.setItem(
        CNXT_TOKEN_EXPIRATION,
        new Date().getTime() + parseInt(tokenResult.expires_in) * 1000
      );
      toRecognitionRoute();
    } else {
      setLoading(false);
      errorLog("unexpected results on positive auth, resetting");
      window.localStorage.removeItem(CNXT_ACCESS_TOKEN);
      window.localStorage.removeItem(CNXT_REFRESH_TOKEN);
      window.localStorage.removeItem(CNXT_TOKEN_EXPIRATION);
    }
  };

  // Called when the user hits submit from the DomainPane
  const attemptDomainLookup = async (value, errorMsgFunc) => {
    debugLog("Set domain: ", value);
    setLoading(true);
    window.localStorage.setItem(CNXT_DOMAIN, value);

    axios
      .get("/api/cnxt/configuration", {
        headers: {
          tenant_id: value,
        },
      })
      .then((config) => {
        debugLog("profile success: ", config);
        errorMsgFunc("");

        axios
          .get("/api/cnxt/branding", {
            headers: {
              tenant_id: value,
            },
          })
          .then((branding) => {
            debugLog(
              "branding success, storing project profile in local storage"
            );
            config.data.branding = branding.data;
            window.localStorage.setItem(
              PROJECT_PROFILE,
              JSON.stringify(config.data)
            );
            if (SetBranding()) {
              state.isBrandingSet = true;
              setState(state);
            }
            setLoading(false);
          })
          .catch((error) => {
            errorLog("Error reading branding service: ", error);
            setLoading(false);
          });

        if (invertSSO) {
          // for testing
          config.data.ssoEnabled = !config.data.teams.sso;
        }

        if (config.data.teams.sso) {
          attemptTeamsAuth(config.data.teams.sso);
        } else {
          state.loginPane = USERPW_PANE;
          state.selectedDomain = value;
          setState(state);
        }
      })
      .catch((error) => {
        setLoading(false);
        errorMsgFunc(intl.formatMessage(messages.alertInvalidUrl, { value }));
      });
  };

  // Called when user hist submit from the User PWPane
  function loginByUser(userName, pwd, errorMsgFunc) {
    var details = {
      tenant_id: window.localStorage.getItem(CNXT_DOMAIN),
      username: userName,
      password: pwd,
    };
    setLoading(true);

    axios
      .post("/api/cnxtoauth/logon", JSON.stringify(details), {
        headers: { "Content-Type": "application/json" },
      })
      .then((data) => {
        window.localStorage.setItem(CNXT_ACCESS_TOKEN, data.data.access_token);
        window.localStorage.setItem(
          PREFERRED_LOCALE,
          data.data.preferredLocale
        );
        window.localStorage.setItem(
          CNXT_REFRESH_TOKEN,
          data.data.refresh_token
        );
        window.localStorage.setItem(
          CNXT_TOKEN_EXPIRATION,
          new Date().getTime() + parseInt(data.data.expires_in) * 1000
        );

        state.loginPane = NONE;
        setState(state);
        toRecognitionRoute();
      })
      .catch((error) => {
        setLoading(false);
        errorMsgFunc(
          intl.formatMessage(messages.alertInvalidUsername, { userName })
        );
        console.log(error.name + " " + error.message);
      });
  }

  const toRecognitionRoute = async () => {
    const dest = !!destination ? destination : "/recognition";
    debugLog("redirecting to: ", dest);
    state.currentLocale = window.localStorage
      .getItem(PREFERRED_LOCALE)
      .replace("_", "-");
    const translations = await state.messagesLoader[state.currentLocale]();
    state.messages = translations;
    state.loggedOn = true;
    setLoading(false);
    setState(state);
    history.push(dest);
  };

  return (
    <div>
      <DomainPane clickFunc={attemptDomainLookup} />
      <UserPWPane clickFunc={loginByUser} />
      <HelpPane />

      {process.env.REACT_APP_HACK_JWT === "true" && (
        <div>
          <input
            type="text"
            value={jwt}
            onChange={jwtOnChange}
            onKeyPress={jwtKeyPress}
            style={{ margin: "10px", width: "100px" }}
          />
          <input id="invert-sso" type="checkbox" onChange={handleInvertSSO} />
          <label for="invert-sso">invert SSO</label>
        </div>
      )}
    </div>
  );
}
